import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Button, Row, Col, Container } from 'react-bootstrap'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import { Link, useNavigate, useParams } from 'react-router-dom'
import fullLogoLight from '../../client/logo/logo-full-light.svg'
import { Messages } from 'primereact/messages'
import { useToast } from '../common/toast/hooks/useToast'
import axios from '../../api/axios'
import { handleAxiosErrors } from '../common/forms/errors/Helper'
import Common from '../common/Common'
import TextField from '../common/forms/fields/TextField'
import useAuth from '../../hooks/useAuth'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

type FormObject = {
    verification_code?: string
    new_password?: string
    new_password_confirmation?: string
}

const validationSchema = Yup.object().shape({
    verification_code: Yup.string().trim().required('Required'),
    new_password: Yup.string().trim().required('Required'),
    new_password_confirmation: Yup.string()
        .trim()
        .required('Required')
        .oneOf([Yup.ref('new_password'), null], 'Passwords must match'),
})

const UpdatePassword: FC = () => {
    const emptyFormObject: FormObject = {
        verification_code: '',
        new_password: '',
        new_password_confirmation: '',
    }

    const [validToken, setValidToken] = useState<boolean>(false)
    const [formObject] = useState<FormObject>(emptyFormObject)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')

    const { setAuth }: any = useAuth()

    const errRef = useRef()
    const msgs = useRef(null)

    const { triggerToast } = useToast()

    const navigate = useNavigate()
    const { token } = useParams()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setIsLoading(true)

        try {
            const response = await axios
                .get(`/auth/validate-token/${token}`)
                .finally(() => {
                    setIsLoading(false)
                })

            if (response.data?.success !== true) {
                throw Error(response.data?.errors.join('<br />'))
            } else {
                setValidToken(true)
            }
        } catch (err) {
            // TODO: Sort out error handling!
            // console.log('err: ', err)
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else if (err.response?.data?.success === false) {
                setErrMsg(err.response?.data?.errors.join('<br />'))
            } else {
                setErrMsg('Failed to update password')
            }
        }
    }, [token])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const initiateAccount = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const response = await axios
                .post(
                    '/auth/update-password',
                    {
                        ...record,
                        token,
                    },
                    {
                        headers: { 'Content-Type': 'application/json' },
                        withCredentials: true,
                    }
                )
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: 'Successful',
                    detail: 'Your password has been updated successfully. You may login now!',
                })
                setAuth({})
                navigate('/login')
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            console.log('err: ', err)
            handleAxiosErrors(err, setErrMsg)
        }
    }

    useEffect(() => {
        setErrMsg('')
    }, [formObject])

    useEffect(() => {
        if (errMsg) {
            msgs.current?.show({
                id: '1',
                sticky: true,
                severity: 'error',
                summary: '',
                detail: errMsg,
                closable: false,
            })
        } else {
            msgs.current?.clear()
        }
    }, [errMsg])

    const formFooter = (
        <>
            <Button
                variant="primary"
                className="rounded-pill btn-lg"
                disabled={isSubmitting}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'app-updatePassword-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            Updating password...
                        </>
                    ) : (
                        <>Update</>
                    )}
                </span>
            </Button>
            <hr />
            <div className="text-center">
                <Link to={'/login'} className="small">
                    Cancel
                </Link>
            </div>
        </>
    )

    return (
        <div>
            {isLoading && <Common.Loader text="Loading..." />}
            <section className="bg-gradient-primary vh-100">
                <Container>
                    <Row className="justify-content-center">
                        <Col md={9} lg={8} xl={7} xxl={6}>
                            <div className="card o-hidden border-0 shadow-lg my-5">
                                <div className="card-body p-0">
                                    <Row>
                                        <Col>
                                            <div className="p-5">
                                                <div className="">
                                                    <div>
                                                        <div className="p-4 text-center w-100 border-bottom border-primary mb-4">
                                                            <img
                                                                src={
                                                                    fullLogoLight
                                                                }
                                                                alt="Full Logo"
                                                                className="full-logo"
                                                            />
                                                        </div>
                                                    </div>
                                                    {validToken ? (
                                                        <h1 className="h4 text-center mb-4">
                                                            Update your password
                                                        </h1>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {isSubmitting && (
                                                        <Common.Loader text="Loading..." />
                                                    )}
                                                    <div
                                                        ref={errRef}
                                                        className={
                                                            'text-danger small ' +
                                                            (errMsg
                                                                ? 'errmsg'
                                                                : 'offscreen')
                                                        }
                                                        aria-live="assertive"
                                                    >
                                                        <Messages ref={msgs} />
                                                    </div>
                                                </div>
                                                <Formik
                                                    initialValues={{
                                                        ...formObject,
                                                    }}
                                                    validationSchema={
                                                        validationSchema
                                                    }
                                                    onSubmit={(
                                                        values,
                                                        formik
                                                    ) => {
                                                        initiateAccount(values)
                                                    }}
                                                    enableReinitialize={true}
                                                >
                                                    {(formikProps) => {
                                                        return (
                                                            <FormikForm>
                                                                <Row>
                                                                    {validToken ? (
                                                                        <>
                                                                            <Col
                                                                                xl={
                                                                                    12
                                                                                }
                                                                            >
                                                                                <TextField
                                                                                    id="formInitiationCode"
                                                                                    name="verification_code"
                                                                                    label="Verification Code"
                                                                                    placeholder="Enter your verification code"
                                                                                    formikProps={
                                                                                        formikProps
                                                                                    }
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                xl={
                                                                                    6
                                                                                }
                                                                            >
                                                                                <TextField
                                                                                    id="formNewPassword"
                                                                                    type="password"
                                                                                    name="new_password"
                                                                                    label="New Password"
                                                                                    placeholder="***********"
                                                                                    formikProps={
                                                                                        formikProps
                                                                                    }
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                xl={
                                                                                    6
                                                                                }
                                                                            >
                                                                                <TextField
                                                                                    id="formNewPasswordConfirmation"
                                                                                    type="password"
                                                                                    name="new_password_confirmation"
                                                                                    label="Confirm New Password"
                                                                                    placeholder="***********"
                                                                                    formikProps={
                                                                                        formikProps
                                                                                    }
                                                                                />
                                                                            </Col>
                                                                        </>
                                                                    ) : (
                                                                        <></>
                                                                    )}
                                                                </Row>
                                                                <input
                                                                    type="submit"
                                                                    id="app-updatePassword-form-submit-btn"
                                                                    className="d-none"
                                                                />
                                                            </FormikForm>
                                                        )
                                                    }}
                                                </Formik>

                                                <br />
                                                {validToken ? (
                                                    <div className="text-center d-grid gap-2">
                                                        {formFooter}
                                                    </div>
                                                ) : (
                                                    <></>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    )
}

export default UpdatePassword
